import React, { useCallback, useState } from 'react';
import { Layout, Skeleton } from 'antd';
import styled from '@emotion/styled';
import useSWR from 'swr';
import { ActivityLogTable, ActivityPageHeader } from '../../organisms/activity';
import { getActivityLogRepository } from '../../../repository/mytm/activity/activityLogRepository';
import { PAGE_SIZE_OPTIONS, PER_PAGE_DEFAULT } from '../../../constants/common';
import { API_URLS } from '../../../constants/urls/api-urls';
import { ActivityLogInterface } from '../../../types/activityLog';
import { DetailActivityLogModal } from '../../organisms/activity/DetailModal';
import { appStyles } from '../../../styles';

interface OptionSearch {
  itemId?: string;
  modifiedBy?: string;
  module?: string;
}

const { Content } = Layout;

export const ActivityLog = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PER_PAGE_DEFAULT);
  const [optionSearch] = useState<OptionSearch>({});
  const [loading] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<ActivityLogInterface>();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const { data, isValidating, error } = useSWR(
    [API_URLS.ACTIVITY_LOG, page, pageSize],
    (url: string, pageNumber: number, pageSizeNumber: number) =>
      getActivityLogRepository(url, {
        page: pageNumber,
        take: pageSizeNumber || PER_PAGE_DEFAULT,
        itemId: optionSearch.itemId,
        modifiedBy: optionSearch.modifiedBy,
        module: optionSearch.module,
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );
  const handleChangePage = useCallback((pg: number, pgSize?: number) => {
    setPage(pg);
    setPageSize(pgSize || PER_PAGE_DEFAULT);
  }, []);

  const onShowSizeChange = useCallback((current: number, size: number) => {
    setPage(current);
    setPageSize(size || PER_PAGE_DEFAULT);
  }, []);

  const onSetCurrentActivityLog = useCallback((record?: ActivityLogInterface) => {
    setIsOpenModal(true);
    setCurrentRecord(record);
  }, []);

  const onCloseModal = useCallback(() => {
    setIsOpenModal(false);
  }, []);

  if (!data || error) {
    return (
      <Container>
        <Skeleton active />
      </Container>
    );
  }

  const recordActivityLog = data.data.map((item: ActivityLogInterface, index: number) => ({ ...item, ...{ index } }));

  return (
    <>
      <Container>
        <Content>
          <ActivityPageHeader />
          <ActivityLogTable
            records={recordActivityLog}
            loading={loading || isValidating}
            onClickViewDetail={onSetCurrentActivityLog}
            pagination={{
              showSizeChanger: true,
              onChange: handleChangePage,
              pageSize,
              current: page,
              total: data.total,
              onShowSizeChange,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
            }}
          />
        </Content>
        <DetailActivityLogModal record={currentRecord} isOpenModal={isOpenModal} onClose={onCloseModal} />
      </Container>
    </>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}

  & .ant-pagination {
    float: right;
    padding: 15px 0 0 0;
  }
`;
